// Spreadsheet-style keyboard navigation + copy/paste helpers for the list view.
//
// Framework-agnostic (plain DOM/JS) so it survives the Vue 2 -> 3 migration.
// The list-view container tags each editable cell with data attributes; every
// function here works off those attributes and the table DOM, with no Vue or
// store coupling.
//
//   data-ql-r     global row index (integer position in the flattened cue rows,
//                 parts sub-rows included) — stable across the virtualization
//                 window so vertical moves stay column-aligned.
//   data-ql-c     column index (position in the list-view's ordered columns).
//                 Composite cells (followHang = select + text) share one column
//                 index so horizontal nav treats them as a single stop.
//   data-ql-cue   cue id
//   data-ql-part  parts sub-row index (absent for non-parts cells)
//   data-ql-attr  the cue field this cell edits (title | id | label | <attr>)
//   data-ql-kind  'text' (contenteditable) | 'select'
//
// Navigation resolves targets over the currently-mounted cells (the 600px
// virtualization overscan keeps ~20 rows on either side mounted, so a single
// step is always mounted); the caller scroll-nudges + retries at the window
// edge. Column layout is sparse (parts rows, per-layer attributes), so we never
// assume a full rectangular grid — we pick the nearest existing cell.

export const CELL_SELECTOR = '[data-ql-r][data-ql-c]';

// Parse a cell element's coordinate + identity. Returns null for non-cells.
export function cellInfo(el) {
    if (!el || !el.getAttribute) return null;
    const r = el.getAttribute('data-ql-r');
    const c = el.getAttribute('data-ql-c');
    if (r === null || c === null) return null;
    const part = el.getAttribute('data-ql-part');
    return {
        el,
        r: parseInt(r, 10),
        c: parseInt(c, 10),
        cueId: el.getAttribute('data-ql-cue'),
        part: part === null ? null : parseInt(part, 10),
        attr: el.getAttribute('data-ql-attr'),
        kind: el.getAttribute('data-ql-kind') || 'text'
    };
}

// The editable cell an event originated from (the element itself or its closest
// cell ancestor — a keystroke inside a contenteditable targets the div directly,
// but be defensive about nested nodes).
export function cellFromTarget(target) {
    if (!target || !target.closest) return null;
    const el = target.matches && target.matches(CELL_SELECTOR)
        ? target
        : target.closest(CELL_SELECTOR);
    return cellInfo(el);
}

function mountedCells(container) {
    return Array.from(container.querySelectorAll(CELL_SELECTOR)).map(cellInfo).filter(Boolean);
}

// Find the mounted cell to move to from `from` in `dir` ('up'|'down'|'left'|'right').
// Returns a cellInfo, or null when there is no such mounted cell (true table edge
// OR the virtualization window edge — the caller distinguishes via atWindowEdge).
export function findTarget(container, from, dir) {
    if (!from) return null;
    const cells = mountedCells(container);
    if (dir === 'left' || dir === 'right') {
        const forward = dir === 'right';
        const cand = cells.filter(x => x.r === from.r && (forward ? x.c > from.c : x.c < from.c));
        if (!cand.length) return null;
        cand.sort((a, b) => (forward ? a.c - b.c : b.c - a.c));
        return cand[0];
    }
    const down = dir === 'down';
    const rowward = cells.filter(x => (down ? x.r > from.r : x.r < from.r));
    if (!rowward.length) return null;
    // Prefer the SAME column. A column before the parts columns is a rowspan cell
    // that exists only on the cue's first sub-row, while parts columns exist on
    // every sub-row — so "nearest row below" would dive into this cue's own part
    // rows and jump sideways. Staying in the same column makes Down land on the
    // next cue's same column (and, within a parts column, on the next part row).
    const sameCol = rowward.filter(x => x.c === from.c);
    if (sameCol.length) {
        const tR = down
            ? Math.min.apply(null, sameCol.map(x => x.r))
            : Math.max.apply(null, sameCol.map(x => x.r));
        return sameCol.filter(x => x.r === tR)[0];
    }
    // Fallback (column doesn't continue): nearest row, nearest column.
    const targetR = down
        ? Math.min.apply(null, rowward.map(x => x.r))
        : Math.max.apply(null, rowward.map(x => x.r));
    const inRow = rowward.filter(x => x.r === targetR);
    inRow.sort((a, b) => Math.abs(a.c - from.c) - Math.abs(b.c - from.c) || a.c - b.c);
    return inRow[0];
}

// True when `from` is the last mounted row in `dir` but not necessarily the last
// row in the whole list — signals the caller to scroll-nudge and retry so a held
// arrow key never dead-ends at the virtualization window boundary.
export function atWindowEdge(container, from, dir) {
    if (dir === 'left' || dir === 'right') return false;
    const cells = mountedCells(container);
    if (!cells.length) return false;
    const rows = cells.map(x => x.r);
    return dir === 'down'
        ? from.r >= Math.max.apply(null, rows)
        : from.r <= Math.min.apply(null, rows);
}

// Focus a cell and place the caret. atStart=true when arriving via Right (so the
// next Right keeps moving within the cell); end otherwise. Selects just focus.
export function focusCell(info, atStart) {
    if (!info || !info.el) return;
    const el = info.el;
    el.focus();
    if (info.kind === 'select') return;
    const sel = window.getSelection && window.getSelection();
    if (!sel) return;
    try {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(!!atStart);
        sel.removeAllRanges();
        sel.addRange(range);
    } catch (e) {
        // selectNodeContents can throw on an empty/detached node — focus is enough.
    }
}

// Scroll `scroller` so `el` sits fully below the pinned header (headerH px tall)
// and above the bottom edge. Manual scrollTop math against the KNOWN inner
// container — never scrollIntoView, which can scroll the wrong ancestor and
// desync the virtualization window.
export function ensureVisible(scroller, el, headerH) {
    if (!scroller || !el) return;
    // Use viewport rects, not offsetTop — the cell's offsetParent is the
    // position:fixed list wrapper, not this inner scroll container, so offsetTop
    // math scrolls the wrong amount and focus slips off-screen.
    const cr = el.getBoundingClientRect();
    const sr = scroller.getBoundingClientRect();
    const relTop = cr.top - sr.top;          // cell top within the scroller viewport
    const relBottom = cr.bottom - sr.top;
    const h = headerH || 0;
    if (relTop < h) {
        scroller.scrollTop -= (h - relTop);
    } else if (relBottom > scroller.clientHeight) {
        scroller.scrollTop += (relBottom - scroller.clientHeight);
    }
}

// --- caret boundary detection (Range-based; robust to multi-node v-html
// content and Safari's Selection quirks — never offset math on textContent) ---

export function caretAtStart(el) {
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0) return true;
    const range = sel.getRangeAt(0);
    if (!range.collapsed) return false;
    const probe = range.cloneRange();
    probe.selectNodeContents(el);
    probe.setEnd(range.startContainer, range.startOffset);
    return probe.toString().length === 0;
}

export function caretAtEnd(el) {
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0) return true;
    const range = sel.getRangeAt(0);
    if (!range.collapsed) return false;
    const probe = range.cloneRange();
    probe.selectNodeContents(el);
    probe.setStart(range.endContainer, range.endOffset);
    return probe.toString().length === 0;
}

// True when there's a real (non-collapsed) text selection inside a cell — then
// Cmd+C/Cmd+V should defer to the browser's native sub-text copy/paste.
export function hasTextSelection() {
    const sel = window.getSelection && window.getSelection();
    return !!(sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed);
}

// --- cell value read (for copy) ---

export function readCellValue(info) {
    if (!info || !info.el) return '';
    if (info.kind === 'select') {
        const s = info.el;
        const opt = s.options[s.selectedIndex];
        return opt ? opt.text : '';
    }
    return info.el.innerText;
}

// --- clipboard TSV (rows = \n, cols = \t) ---

export function serializeGrid(values2d) {
    return values2d.map(row => row.join('\t')).join('\n');
}

export function parseTSV(text) {
    const clean = String(text == null ? '' : text).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const trimmed = clean.replace(/\n$/, '');
    return trimmed.split('\n').map(line => line.split('\t'));
}



// WEBPACK FOOTER //
// ./src/lib/listViewCellNav.js